import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  PURCHASE_ORDER_STATUS_LABEL,
  PurchaseOrderStatus,
  PurchaseOrderStatusSummary,
} from "../../types/purchase-order.type";
import { numberWithCommas } from "../../utils";
import {
  PO_COMPACT_MEDIA,
  PO_UNFILTERED,
  PURCHASE_ORDER_STATUS_ACCENT,
  PURCHASE_ORDER_STATUS_ORDER,
} from "./data";

/** คำอธิบายใต้การ์ดแดง — ใบที่ยังไม่จับคู่ร้านส่งให้สาขาได้ ไม่ใช่ตัวบล็อก */
const UNMATCHED_HINT = "ส่งให้สาขาได้เลย สาขาจับคู่ร้านเองที่จอ PO ของสาขา";

/** คำอธิบายใต้การ์ดม่วง — งานของ HO เอง ใบกลุ่มนี้ถูกข้ามตอนส่งให้สาขา */
const NO_BRANCH_HINT = "ติ๊กเลือกแล้วกด ระบุสาขา ก่อนส่งให้สาขา";

/** คำอธิบายใต้การ์ดสาขาส่งคืน — HO ย้ายสาขาหรือส่งกลับไปใหม่ */
const RETURNED_HINT = "ดูเหตุผลที่แถว แล้วย้ายสาขาหรือส่งกลับไปใหม่";

const STATUS_HINT: Partial<Record<PurchaseOrderStatus, string>> = {
  NO_BRANCH: NO_BRANCH_HINT,
  RETURNED: RETURNED_HINT,
  UNMATCHED_CUSTOMER: UNMATCHED_HINT,
};

interface StatusSummaryCardProps {
  status: PurchaseOrderStatus;
  count: number;
  active: boolean;
  onSelect: (status: PurchaseOrderStatus) => void;
}

function StatusSummaryCard({
  status,
  count,
  active,
  onSelect,
}: StatusSummaryCardProps) {
  const handleClick = () => {
    onSelect(status);
  };

  return (
    <Paper
      onClick={handleClick}
      sx={{
        flex: "1 1 160px",
        cursor: "pointer",
        borderLeft: 4,
        borderLeftStyle: "solid",
        borderLeftColor: PURCHASE_ORDER_STATUS_ACCENT[status],
        boxShadow: active ? 4 : 1,
        [PO_COMPACT_MEDIA]: { p: 1.5 },
      }}
    >
      <Typography
        variant="h5"
        fontWeight={700}
        color={PURCHASE_ORDER_STATUS_ACCENT[status]}
      >
        {numberWithCommas(count)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {PURCHASE_ORDER_STATUS_LABEL[status]}
      </Typography>
      {STATUS_HINT[status] && (
        <Typography variant="caption" color="text.secondary">
          {STATUS_HINT[status]}
        </Typography>
      )}
    </Paper>
  );
}

interface StatusSummaryBarProps {
  summary: PurchaseOrderStatusSummary;
  /** สถานะที่กรองอยู่ หรือ PO_UNFILTERED เมื่อยังไม่ได้กรอง */
  activeStatus: string;
  onSelectStatus: (status: string) => void;
}

/**
 * แถบสรุปสถานะของรายการ PO (W1)
 *
 * ตัวเลขทุกค่ามาจาก API และนับทั้งชุดที่ตัวกรองครอบคลุม ไม่ใช่เฉพาะหน้าปัจจุบัน
 * (backend ตัดตัวกรองสถานะออกตอนนับ) · กดการ์ดเพื่อกรองเฉพาะสถานะนั้น กดซ้ำเพื่อเลิกกรอง
 */
export default function StatusSummaryBar({
  summary,
  activeStatus,
  onSelectStatus,
}: StatusSummaryBarProps) {
  const handleSelectStatus = (status: PurchaseOrderStatus) => {
    onSelectStatus(status === activeStatus ? PO_UNFILTERED : status);
  };

  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      gap={2}
      mb={2}
      sx={{ [PO_COMPACT_MEDIA]: { gap: 1.5, mb: 1.5 } }}
    >
      {PURCHASE_ORDER_STATUS_ORDER.map((status) => (
        <StatusSummaryCard
          key={status}
          status={status}
          count={summary[status]}
          active={status === activeStatus}
          onSelect={handleSelectStatus}
        />
      ))}
    </Stack>
  );
}
